import { Component, OnInit, forwardRef, inject, DestroyRef, input, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
// Migrated 1:1 from @nexato/nx-core-module (issue/components/updateLocation/issue-location-widget-input.component.ts, #683)
import { EditSelectable } from '../../edit-select-form-control/edit-select-form-control.component';
import { LocationService } from '../../../services/location/location.service';
import { Location as LocationEntity } from '../../../entities/location/location';

@Component({
  selector: 'nx-issue-location-widget-input',
  templateUrl: './issue-location-widget-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IssueLocationWidgetInputComponent),
      multi: true
    }
  ]
})
export class IssueLocationWidgetInputComponent implements OnInit, ControlValueAccessor {

  label = input<string>('Niederlassung');
  placeholder = input<string>('Niederlassung auswählen');
  showLabel = input<boolean>(true);

  public options: EditSelectable[] = [];

  selectedControl = new FormControl<string | null>(null);
  form = new FormGroup({
    selected: this.selectedControl
  });

  // Injected services
  private readonly locationService = inject(LocationService);
  private readonly destroyRef = inject(DestroyRef);

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    // Load locations on init
    effect(() => {
      this.loadLocations();
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    // Subscribe to form changes and emit location ID
    this.selectedControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(selectedId => {
        this.onChange(selectedId);
        this.onTouched();
      });
  }

  /**
   * Remote filter callback for dropdown
   */
  onFilter = (filterValue: string) => {
    // For now, we load all locations and let the dropdown filter locally
    // Can be enhanced later to filter server-side if needed
    console.log('Filter triggered with value:', filterValue);
  };

  /**
   * Load available locations from backend
   */
  private loadLocations(): void {
    this.locationService.getLocations()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(locations => locations.map(location => ({
          id: location.id,
          name: location.name
        }))),
        catchError(error => {
          console.error('Error loading locations:', error);
          return of([]);
        })
      )
      .subscribe(options => {
        this.options = options;
      });
  }

  // ControlValueAccessor implementation

  writeValue(value: string | null): void {
    this.selectedControl.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.selectedControl.disable();
    } else {
      this.selectedControl.enable();
    }
  }
}
